import React, { useState, useEffect } from "react";
import { Table, Loader, Button, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnvironmentalRiskMatrixIso14091form } from "../../../../api";
import { BasicModal, ConfirmModal } from "../../../Shared";
import { Iso14091Form } from "../Iso14091Form";
import { getLevelColor } from "../riskMatrixLevel";
import { RISK_MATRIX_EXAMPLES, loadRiskMatrixExamples } from "../riskMatrixExamples";
import { downloadRiskMatrixReport } from "../riskMatrixReport";
import "../ListEnvironmentalRiskMatrixForms/ListEnvironmentalRiskMatrixForms.scss";

const environmentalRiskMatrixIso14091Ctrl = new EnvironmentalRiskMatrixIso14091form();

const CRITICAL_THRESHOLD = 15;

const CSV_COLUMNS = [
  "title", "driver", "chain", "exposure", "sensitivity", "adaptiveCapacity",
  "severity", "level", "controls", "action",
];

function toCSV(items) {
  const escapeCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = CSV_COLUMNS.join(",");
  const rows = items.map((item) => CSV_COLUMNS.map((col) => escapeCell(item[col])).join(","));
  return [header, ...rows].join("\n");
}

function downloadCSV(items) {
  const csv = toCSV(items);
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `matriz_riesgos_iso14091_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function Iso14091List({ siteSelected, reload, onReload, embedded, onOpenAnalysis, onItemsCount }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [items, setItems] = useState(null);
  const [editItem, setEditItem] = useState(undefined);
  const [showForm, setShowForm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loadingExamples, setLoadingExamples] = useState(false);

  useEffect(() => {
    (async () => {
      if (!siteSelected) {
        setItems([]);
        return;
      }
      try {
        const response = await environmentalRiskMatrixIso14091Ctrl.getEnvironmentalRiskMatricesIso14091BySite(accessToken, siteSelected);
        setItems(response.code === 200 ? response.riskMatrices || [] : []);
      } catch (error) {
        console.error(error);
        setItems([]);
      }
    })();
  }, [siteSelected, reload, accessToken]);

  useEffect(() => {
    if (items) onItemsCount?.(items.length);
  }, [items, onItemsCount]);

  const openCreate = () => {
    setEditItem(undefined);
    setShowForm(true);
  };

  const handleLoadExamples = async () => {
    setLoadingExamples(true);
    try {
      await loadRiskMatrixExamples({
        accessToken,
        siteSelected,
        examples: RISK_MATRIX_EXAMPLES.iso14091,
        createFn: (token, payload) => environmentalRiskMatrixIso14091Ctrl.createEnvironmentalRiskMatrixIso14091(token, payload),
      });
      toast.success(t("risk_matrix_examples_loaded"));
      onReload();
    } catch (error) {
      toast.error(error?.msg || error?.message || t("server_connection_error"));
    } finally {
      setLoadingExamples(false);
    }
  };
  const openEdit = (item) => {
    setEditItem(item);
    setShowForm(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      const res = await environmentalRiskMatrixIso14091Ctrl.deleteEnvironmentalRiskMatrixIso14091(accessToken, pendingDelete._id);
      if (res.code === 200) {
        toast.success(t("risk_matrix_item_deleted"));
        onReload();
      }
    } catch (error) {
      toast.error(error?.msg || error?.message || t("server_connection_error"));
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  if (!items) return <Loader active inline="centered" />;

  const criticalCount = items.filter((i) => i.level >= CRITICAL_THRESHOLD).length;
  const sortedByLevel = items.slice().sort((a, b) => b.level - a.level);
  const top3 = sortedByLevel.slice(0, 3);

  const handleDownloadReport = () => {
    if (items.length === 0) {
      toast.info(`${t("risk_matrix_empty_in")} ${t("risk_matrix_methodology_iso14091")}.`);
      return;
    }
    downloadRiskMatrixReport({
      t,
      filenamePrefix: "iso14091",
      tag: t("risk_matrix_methodology_iso14091"),
      title: t("risk_matrix_methodology_iso14091_desc"),
      intro: t("risk_matrix_methodology_iso14091_intro"),
      statCount: items.length,
      statLabel: t("risk_matrix_stat_items"),
      critCount: criticalCount,
      critLabel: t("risk_matrix_stat_critical"),
      oppCount: null,
      oppLabel: null,
      columns: [
        { key: "title", label: t("title") },
        { key: "driver", label: t("risk_matrix_iso14091_driver") },
        { key: "chain", label: t("risk_matrix_iso14091_chain") },
        { key: "severity", label: t("risk_matrix_iso14091_vulnerability") },
        { key: "level", label: t("risk_matrix_level") },
      ],
      sortedItems: sortedByLevel,
      items,
      hasHeatMap: true,
    });
  };

  return (
    <div className="risk-matrix-list">
      {!embedded && (
        <>
          <div className="risk-matrix-list__head">
            <div>
              <span className="risk-matrix-list__tag">{t("risk_matrix_methodology_iso14091")}</span>
              <h2>{t("risk_matrix_methodology_iso14091_desc")}</h2>
            </div>
            <div className="risk-matrix-list__head-actions">
              <Button className="risk-matrix-list__btn risk-matrix-list__btn--solid" onClick={handleDownloadReport}>
                <Icon name="file alternate outline" /> {t("risk_matrix_download_report")}
              </Button>
              {onOpenAnalysis && (
                <Button className="risk-matrix-list__btn risk-matrix-list__btn--outline" onClick={onOpenAnalysis}>
                  {t("risk_matrix_open_analysis")} →
                </Button>
              )}
            </div>
          </div>
          <p className="risk-matrix-list__intro">{t("risk_matrix_methodology_iso14091_intro")}</p>
        </>
      )}

      {embedded && items.length > 0 && (
        <div className="risk-matrix-list__toolbar">
          <Button className="risk-matrix-list__btn risk-matrix-list__btn--solid" onClick={openCreate}>
            <Icon name="plus" /> {t("risk_matrix_new_item")}
          </Button>
          <Button className="risk-matrix-list__btn risk-matrix-list__btn--outline" onClick={() => downloadCSV(items)}>
            <Icon name="download" /> {t("risk_matrix_export_csv")}
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <div className="risk-matrix-list__stats">
          <span className="risk-matrix-list__stat">
            <b>{items.length}</b> {t("risk_matrix_stat_items")}
          </span>
          <span className="risk-matrix-list__stat">
            <b>{criticalCount}</b> {t("risk_matrix_stat_critical")}
          </span>
        </div>
      )}

      {top3.length > 0 && (
        <div className="risk-matrix-list__top3">
          <h4>{t("risk_matrix_top_critical_title")}</h4>
          {top3.map((item) => (
            <div key={item._id} className="risk-matrix-list__top3-row">
              <span>{item.title}</span>
              <span className="risk-matrix-list__level" style={{ background: getLevelColor(item.level) }}>
                {item.level}
              </span>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 ? (
        <div className="risk-matrix-list__empty-state">
          <p>
            {t("risk_matrix_empty_in")} {t("risk_matrix_methodology_iso14091")}.
          </p>
          <div className="risk-matrix-list__empty-actions">
            <Button className="risk-matrix-list__btn risk-matrix-list__btn--solid" onClick={openCreate}>
              {t("risk_matrix_empty_open_first")} →
            </Button>
            <Button
              className="risk-matrix-list__btn risk-matrix-list__btn--outline"
              loading={loadingExamples}
              disabled={loadingExamples}
              onClick={handleLoadExamples}
            >
              {t("risk_matrix_load_examples")}
            </Button>
          </div>
        </div>
      ) : (
        <div className="risk-matrix-list__scrollx">
          <Table celled sortable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>{t("title")}</Table.HeaderCell>
                <Table.HeaderCell>{t("risk_matrix_iso14091_driver")}</Table.HeaderCell>
                <Table.HeaderCell>{t("risk_matrix_iso14091_chain")}</Table.HeaderCell>
                <Table.HeaderCell>{t("risk_matrix_iso14091_vulnerability")}</Table.HeaderCell>
                <Table.HeaderCell>{t("risk_matrix_level")}</Table.HeaderCell>
                <Table.HeaderCell>{t("actions")}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((item) => (
                <Table.Row key={item._id}>
                  <Table.Cell>{item.title}</Table.Cell>
                  <Table.Cell>{item.driver}</Table.Cell>
                  <Table.Cell>{item.chain}</Table.Cell>
                  <Table.Cell>{item.severity}</Table.Cell>
                  <Table.Cell>
                    <span className="risk-matrix-list__level" style={{ background: getLevelColor(item.level) }}>
                      {item.level}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="risk-matrix-list__row-actions">
                      <Button
                        icon
                        size="small"
                        className="risk-matrix-list__row-btn risk-matrix-list__row-btn--edit"
                        onClick={() => openEdit(item)}
                        title={t("edit")}
                      >
                        <Icon name="pencil" />
                      </Button>
                      <Button
                        icon
                        size="small"
                        className="risk-matrix-list__row-btn risk-matrix-list__row-btn--delete"
                        onClick={() => setPendingDelete(item)}
                        title={t("delete")}
                      >
                        <Icon name="trash" />
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      )}

      <BasicModal show={showForm} close={() => setShowForm(false)} size="small" className="risk-matrix-modal">
        <Iso14091Form item={editItem} siteSelected={siteSelected} onClose={() => setShowForm(false)} onReload={onReload} />
      </BasicModal>

      <ConfirmModal
        open={!!pendingDelete}
        title={t("risk_matrix_delete_confirm_title")}
        content={pendingDelete?.title}
        loading={deleting}
        confirmText={t("delete")}
        cancelText={t("cancel")}
        danger
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
